/**
 * shared/weapons/hitloc.ts — hit locations (T6 hitLocation_t / GSC `shitloc`) and the per-weapon
 * location damage multipliers.
 *
 * OWNER: WEAPONS agent.
 *
 * Order follows the WEAPONFILE loc* fields (research/weapons-mp.md §2.1 "hit-location multipliers",
 * data/weapons_mp.json `fields.damage.loc*`): T6 adds torso_mid to T5's hitLocation_t
 * (tools/src/KisakBlack/src/qcommon/common.h HITLOC_*). Numeric values are ours (u8 if ever networked).
 */
import { WeaponId } from '../types';
import { getWeaponDef } from './defs';

export const HitLoc = {
  NONE: 0,
  HELMET: 1,
  HEAD: 2,
  NECK: 3,
  TORSO_UPPER: 4,
  TORSO_MID: 5,
  TORSO_LOWER: 6,
  R_ARM_UPPER: 7,
  L_ARM_UPPER: 8,
  R_ARM_LOWER: 9,
  L_ARM_LOWER: 10,
  R_HAND: 11,
  L_HAND: 12,
  R_LEG_UPPER: 13,
  L_LEG_UPPER: 14,
  R_LEG_LOWER: 15,
  L_LEG_LOWER: 16,
  R_FOOT: 17,
  L_FOOT: 18,
  GUN: 19,
} as const;
export type HitLoc = (typeof HitLoc)[keyof typeof HitLoc];

/** GSC shitloc strings ("head", "torso_upper", ...), indexed by HitLoc. */
export const HIT_LOC_NAMES: readonly string[] = [
  'none', 'helmet', 'head', 'neck', 'torso_upper', 'torso_mid', 'torso_lower',
  'right_arm_upper', 'left_arm_upper', 'right_arm_lower', 'left_arm_lower', 'right_hand', 'left_hand',
  'right_leg_upper', 'left_leg_upper', 'right_leg_lower', 'left_leg_lower', 'right_foot', 'left_foot', 'gun',
];

/** WEAPONFILE field name of each location's multiplier, indexed by HitLoc. */
const LOC_FIELDS: readonly string[] = [
  'locNone', 'locHelmet', 'locHead', 'locNeck', 'locTorsoUpper', 'locTorsoMid', 'locTorsoLower',
  'locRightArmUpper', 'locLeftArmUpper', 'locRightArmLower', 'locLeftArmLower', 'locRightHand', 'locLeftHand',
  'locRightLegUpper', 'locLeftLegUpper', 'locRightLegLower', 'locLeftLegLower', 'locRightFoot', 'locLeftFoot', 'locGun',
];

const cache: number[][] = [];

/**
 * Location damage multiplier for `weapon` at `loc`.
 * - crossbow_mp: the attachment unique au_crossbow_none table (head/helmet 4, neck 5, torso 1/1/0.9, arms
 *   0.8/0.6/0.4, legs 0.8/0.6/0.4) [WPN §2.1]. T6 stores locationDamageMultipliers on the attachment unique;
 *   whether they apply to MOD_IMPACT is open for T6 [WPN §7.4]. We apply them: in T5, G_MissileImpact passes
 *   weapDef->damage to G_Damage, and G_DamageClient then multiplies every non-MOD_MELEE hit (MOD_IMPACT included)
 *   by G_GetWeaponHitLocationMultiplier, which returns locationDamageMultipliers for WEAPTYPE_PROJECTILE/GRENADE
 *   (KisakBlack game_mp/g_combat_mp.cpp:407-430, 497-501). So a head (30x4=120) or neck (30x5=150) bolt impact
 *   kills before the 1 s fuse; the bolt then rests on the corpse (combat.ts CORPSE_STUCK_MAX_Z).
 * - every other SnS weapon: its own loc* fields (all 1 for the knife/hatchet/bolt) [WPN §3, §4].
 * - HitLoc.NONE (splash, world) is always 1.
 */
export function locationMultiplier(weapon: WeaponId, loc: number): number {
  if (loc <= 0 || loc >= LOC_FIELDS.length) return 1;
  let t = cache[weapon];
  if (!t) {
    const raw = getWeaponDef(weapon).raw;
    t = LOC_FIELDS.map((f) => {
      const au = raw[`au_${f}`];
      if (typeof au === 'number') return au;
      const v = raw[f];
      return typeof v === 'number' ? v : 1;
    });
    cache[weapon] = t;
  }
  return t[loc];
}

export function isHeadLoc(loc: number): boolean {
  return loc === HitLoc.HEAD || loc === HitLoc.HELMET;
}

/**
 * _globallogic_utils.gsc:355-368 isheadshot(): head/helmet only; never MOD_MELEE/MOD_BAYONET; MOD_IMPACT only
 * for knife_ballistic_mp. `modIsImpact` / `modIsMelee` keep this file free of the MeansOfDeath numbering.
 */
export function isHeadshot(weapon: WeaponId, loc: number, modIsImpact: boolean, modIsMelee: boolean): boolean {
  if (!isHeadLoc(loc)) return false;
  if (modIsMelee) return false;
  if (modIsImpact && weapon !== WeaponId.KNIFE_BALLISTIC) return false;
  return true;
}
